import type { ComponentType } from "react";
import { Link } from "react-router-dom";
import { BarChart3, ClipboardCheck, ListOrdered, Rocket, Users } from "lucide-react";
import type { WorkspaceTab } from "@/lib/emailRoutes";
import { MAIN_BG } from "@/lib/constants";
import { formatNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";

export interface WorkspaceNavItem {
  key: WorkspaceTab;
  label: string;
  to: string;
  /** A count beside the label; hidden while unknown. */
  count?: number | null;
  /** One line on the section's state ("2 ready to launch"). */
  summary: string;
  /** Draws the eye: work waits there (leads ready to launch). */
  attention?: boolean;
}

const ICONS: Record<WorkspaceTab, ComponentType<{ className?: string }>> = {
  sequence: ListOrdered,
  leads: Users,
  launch: Rocket,
  performance: BarChart3,
  review: ClipboardCheck,
};

/**
 * A campaign's sections, in the order of the work — write the Sequence, add
 * Leads, Launch them, read the Performance. Each one a link (the URL is the
 * state), always in view: the bar stays under the app header while the page
 * scrolls.
 */
export function WorkspaceNav({ value, items, compact = false }: { value: WorkspaceTab; items: WorkspaceNavItem[]; compact?: boolean }) {
  return (
    <nav
      aria-label="Campaign sections"
      className="sticky top-16 z-20 -mx-2 px-2 py-2 backdrop-blur-md"
      style={{ backgroundColor: `${MAIN_BG}e6` }}
    >
      <ol className={cn("grid grid-cols-2 gap-1.5 rounded-plane bg-white p-1.5 shadow-plane", items.length === 5 ? "md:grid-cols-5" : "md:grid-cols-4")}>
        {items.map((item, index) => {
          const active = item.key === value;
          const Icon = ICONS[item.key];
          return (
            <li key={item.key} className="min-w-0">
              <Link
                to={item.to}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "group flex h-full min-w-0 items-center gap-3 rounded-[10px] px-3 outline-none transition-colors",
                  compact ? "py-2" : "py-2.5",
                  "focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1",
                  active ? "bg-indigo-600 text-white shadow-cta" : "text-slate-700 hover:bg-indigo-50/60",
                )}
              >
                <span
                  className={cn(
                    "relative flex shrink-0 items-center justify-center rounded-lg transition-colors",
                    compact ? "h-6 w-6" : "h-9 w-9",
                    active ? "bg-white/15 text-white" : "bg-indigo-50 text-indigo-600 group-hover:bg-white",
                  )}
                >
                  <Icon className="h-[18px] w-[18px]" />
                  {item.attention && !active && (
                    <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-white" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    {!compact && <span
                      className={cn(
                        "hidden text-[11px] font-semibold tabular-nums lg:inline",
                        active ? "text-indigo-200" : "text-slate-300",
                      )}
                      aria-hidden
                    >
                      {index + 1}
                    </span>}
                    <span className="truncate text-sm font-semibold">{item.label}</span>
                    {!compact && item.count !== undefined && item.count !== null && (
                      <span
                        className={cn(
                          "rounded-full px-1.5 py-px text-[11px] font-semibold tabular-nums",
                          active
                            ? "bg-white/20 text-white"
                            : item.attention
                              ? "bg-amber-100 text-amber-800"
                              : "bg-slate-100 text-slate-500",
                        )}
                      >
                        {formatNumber(item.count)}
                      </span>
                    )}
                  </span>
                  {!compact && <span className={cn("block truncate text-xs", active ? "text-indigo-100" : "text-slate-500")}>
                    {item.summary}
                  </span>}
                </span>
              </Link>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
